/**
 * PvP normalization (docs/systems/ITEMS_V2.md §4.3, the user's proposal 2026-10-08 "partial"): in the ranked ladder
 * (`modes: pvp`) every piece counts with the same base stats and the same +forge, so gear decides *how* you fight, not
 * *whether* you win. Affixes, gems, sets and uniques keep working in full. In guild war only the forge is capped.
 * The open world, bosses and dungeons are not normalized.
 *
 * Pure rules: `equipmentStats(equipment, rolls, pvpNormalizer('ranked'))` is the sum under the template; the World
 * picks the rule from the bout under way (`pvpRuleOf`, systems/arena.ts).
 */
import { armourPlusStats, FORGE_SLOTS, FORGE_MAX, type WeaponForge } from './forge';
import { fullStats, ITEMS, type EquipSlot, type GearNormalizer, type ItemDef, type ItemStats } from './items';

export type PvpRule = 'ranked' | 'guildWar';

export const PVP_TEMPLATE = {
  /** Ranked: every forgeable piece counts as +this (the element and its level are kept). */
  plus: 8,
  /** Guild war: a piece counts at most as +this. */
  guildWarPlusCap: 10,
  /** Ranked: the base stats of a piece, by slot (a rare-grade piece). Slots not listed bring no base stats. */
  base: {
    weapon: { atk: 12 },
    head: { def: 4, maxHp: 30 },
    body: { def: 8, maxHp: 40 },
    legs: { def: 4, maxHp: 25 },
    feet: { def: 3, maxHp: 20 },
  } as Readonly<Partial<Record<EquipSlot, ItemStats>>>,
} as const;

const r6 = (x: number): number => Math.round(x * 1e6) / 1e6;

/** What a piece adds beyond its base: affixes and gems (a pre-v2 roll: the delta to the base). */
function affixPart(it: ItemDef, baseId: string): ItemStats {
  const base = ITEMS[baseId]?.stats ?? {};
  const out: ItemStats = {};
  for (const k of new Set([...Object.keys(it.stats), ...Object.keys(base)]) as Set<keyof ItemStats>) {
    const d = r6((it.stats[k] ?? 0) - (base[k] ?? 0));
    if (d) out[k] = d;
  }
  return out;
}

const add = (a: ItemStats, b: ItemStats): ItemStats => {
  const s: ItemStats = { ...a };
  for (const k of Object.keys(b) as (keyof ItemStats)[]) s[k] = r6((s[k] ?? 0) + (b[k] ?? 0));
  return s;
};

/** The forge a piece counts with under a rule. */
export function pvpForge(rule: PvpRule, slot: EquipSlot, forge: WeaponForge | undefined): WeaponForge | undefined {
  if (rule === 'guildWar')
    return forge && forge.plus > PVP_TEMPLATE.guildWarPlusCap
      ? { ...forge, plus: PVP_TEMPLATE.guildWarPlusCap }
      : forge;
  if (!FORGE_SLOTS.includes(slot)) return forge;
  return { plus: Math.min(PVP_TEMPLATE.plus, FORGE_MAX), el: forge?.el ?? null, lv: forge?.lv ?? 0 };
}

/** The `equipmentStats` normalizer of a rule. */
export function pvpNormalizer(rule: PvpRule): GearNormalizer {
  return (it, roll) => {
    const forge = pvpForge(rule, it.slot, it.forge);
    if (rule === 'guildWar') {
      const { forge: _f, ...rest } = it;
      void _f;
      return fullStats(forge ? { ...rest, forge } : rest);
    }
    const tpl = PVP_TEMPLATE.base[it.slot] ?? {};
    const baseId = roll?.base ?? it.id;
    const { forge: _f, ...rest } = it;
    void _f;
    const lvl = fullStats({ ...rest, stats: tpl, ...(forge ? { forge } : {}) });
    return add(lvl, affixPart(it, baseId));
  };
}

/** Armour plus kept exported for tests: the template's +forge on a slot's template base. */
export const templateStats = (slot: EquipSlot): ItemStats => {
  const tpl = PVP_TEMPLATE.base[slot] ?? {};
  return FORGE_SLOTS.includes(slot) && slot !== 'weapon' ? add(tpl, armourPlusStats(tpl, PVP_TEMPLATE.plus)) : tpl;
};

/**
 * The rule in force for a World: ranked while a ladder bout in `pvp` mode is matched (countdown, fight, result),
 * else guild war while the player is on the field of a war against players, else none (the open world is never normalized).
 */
export function pvpRuleOf(w: {
  readonly duel: { readonly ladder: unknown; readonly mode?: string; readonly phase: string } | null;
}): PvpRule | null {
  const d = w.duel;
  if (d && d.ladder && d.mode === 'pvp' && d.phase !== 'queue') return 'ranked';
  return GUILD_WAR_FIGHT.has(w) ? 'guildWar' : null;
}

/** Worlds whose player stands on the field of a guild war against players (systems/guildField.ts sets and clears). */
export const GUILD_WAR_FIGHT = new WeakSet<object>();

/** The normalizer for `equipmentStats`, or undefined when no rule is in force. */
export const pvpNormalizerOf = (w: Parameters<typeof pvpRuleOf>[0]): GearNormalizer | undefined => {
  const rule = pvpRuleOf(w);
  return rule ? pvpNormalizer(rule) : undefined;
};
